import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@saas-kit/ui';
import type { DataTableColumn } from '@saas-kit/ui/DataTable';

import type { AdminPlan } from './api';
import { PlanStatusBadge } from './PlanStatusBadge';

interface Options {
  onEdit: (plan: AdminPlan) => void;
  onArchive: (plan: AdminPlan) => void;
}

export function usePlanColumns({
  onEdit,
  onArchive,
}: Options): DataTableColumn<AdminPlan>[] {
  const { t } = useTranslation('adminPlans');

  return useMemo<DataTableColumn<AdminPlan>[]>(
    () => [
      {
        id: 'code',
        header: t('table.code'),
        cell: (row) => <span className="font-mono text-xs">{row.code}</span>,
      },
      {
        id: 'name',
        header: t('table.name'),
        cell: (row) => <span className="font-medium">{row.name}</span>,
      },
      {
        id: 'interval',
        header: t('table.interval'),
        cell: (row) => t(`intervals.${row.billing_interval}`),
        exportValue: (row) => row.billing_interval,
      },
      {
        id: 'price',
        header: t('table.price'),
        align: 'end',
        cell: (row) => `${row.unit_amount} ${row.currency}`,
        exportValue: (row) => `${row.unit_amount} ${row.currency}`,
      },
      {
        id: 'trial',
        header: t('table.trial'),
        align: 'end',
        cell: (row) => (row.trial_days > 0 ? `${row.trial_days}d` : '—'),
        exportValue: (row) => String(row.trial_days),
        hideOnMobile: true,
      },
      {
        id: 'status',
        header: t('table.status'),
        cell: (row) => <PlanStatusBadge active={row.is_active} />,
        exportValue: (row) => (row.is_active ? 'active' : 'archived'),
      },
      {
        id: 'actions',
        header: t('table.actions'),
        align: 'end',
        exportSkip: true,
        cell: (row) => (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => onEdit(row)}>
              {t('actions.edit')}
            </Button>
            {row.is_active ? (
              <Button size="sm" variant="ghost" onClick={() => onArchive(row)}>
                {t('actions.archive')}
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [t, onEdit, onArchive],
  );
}
